
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { DataTable } from './DataTable';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface SystemReliabilityScoreProps {
  incidents: any[];
  catalogTasks: any[];
}

export function SystemReliabilityScore({ incidents, catalogTasks }: SystemReliabilityScoreProps) {
  const reliabilityData = React.useMemo(() => {
    // Helper function to extract API names
    const extractApiName = (text: string): string | null => {
      if (!text) return null;
      
      const apiRegex = /\b([a-z0-9-]+[-]?api)\b/i;
      const systemRegex = /\b([a-z0-9-]+[-](?:sys|prc|exp))\b/i;
      
      const apiMatch = text.match(apiRegex);
      if (apiMatch) return apiMatch[0];
      
      const systemMatch = text.match(systemRegex);
      if (systemMatch) return systemMatch[0];
      
      return null;
    };
    
    // Group tickets by API/system
    const systemData: Record<string, {
      name: string;
      incidentCount: number;
      deploymentCount: number;
      criticalIncidents: number;
      uptime: number;
      score: number;
    }> = {};
    
    // Process incidents
    incidents.forEach(incident => {
      const description = incident.short_description || incident.description || "";
      const apiName = extractApiName(description) || "Unknown System";
      
      if (!systemData[apiName]) {
        systemData[apiName] = {
          name: apiName,
          incidentCount: 0,
          deploymentCount: 0,
          criticalIncidents: 0,
          uptime: 99.9, // Default
          score: 0
        };
      }
      
      systemData[apiName].incidentCount += 1;
      
      const priority = (incident.priority || "").toLowerCase();
      if (priority === "1" || priority === "critical" || priority === "high") {
        systemData[apiName].criticalIncidents += 1;
      }
    });
    
    // Process catalog tasks (deployments)
    catalogTasks.forEach(task => {
      const description = task.short_description || task.description || "";
      if (description.toLowerCase().includes("deploy") || description.toLowerCase().includes("release")) {
        const apiName = extractApiName(description) || "Unknown System";
        
        if (!systemData[apiName]) {
          systemData[apiName] = {
            name: apiName,
            incidentCount: 0,
            deploymentCount: 0,
            criticalIncidents: 0,
            uptime: 99.9,
            score: 0
          };
        }
        
        systemData[apiName].deploymentCount += 1;
      }
    });
    
    // Calculate reliability score (100-point scale)
    // Formula: 100 - (incidents * 3) - (critical * 5)
    Object.values(systemData).forEach(system => {
      // Calculate estimated uptime based on incidents
      const incidentImpact = system.criticalIncidents * 0.05 + (system.incidentCount - system.criticalIncidents) * 0.01;
      system.uptime = Math.max(99 - incidentImpact, 90).toFixed(2) as unknown as number;
      
      // Calculate overall reliability score
      system.score = Math.max(0, Math.min(100, 100 - (system.incidentCount * 3) - (system.criticalIncidents * 5)));
      system.score = Math.round(system.score);
    });
    
    return Object.values(systemData)
      .filter(system => system.name !== "Unknown System")
      .sort((a, b) => a.score - b.score);
  }, [incidents, catalogTasks]);

  const columns = [
    {
      key: "name",
      header: "System/API",
      cell: (item: any) => item.name,
    },
    {
      key: "incidentCount",
      header: "Incidents",
      cell: (item: any) => item.incidentCount,
    },
    {
      key: "criticalIncidents",
      header: "Critical",
      cell: (item: any) => item.criticalIncidents,
    },
    {
      key: "uptime",
      header: "Est. Uptime",
      cell: (item: any) => (
        <span className="font-medium">{item.uptime}%</span>
      ),
    },
    {
      key: "score",
      header: "Reliability",
      cell: (item: any) => {
        const getColor = (score: number) => {
          if (score >= 90) return "bg-green-500";
          if (score >= 70) return "bg-yellow-500";
          return "bg-red-500";
        };
        
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div 
                    className={`h-2.5 rounded-full ${getColor(item.score)}`} 
                    style={{ width: `${item.score}%` }}
                  ></div>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{item.score}/100 points</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
    },
  ];

  return (
    <Card>
     
      <CardContent>
        <DataTable 
          data={reliabilityData} 
          columns={columns} 
        />
      </CardContent>
    </Card>
  );
}
